import { useFlags } from 'launchdarkly-react-client-sdk';
import { useRef } from 'react';

import { FEATURE_FLAG_DEFAULTS } from '@/constants';
import { FeatureFlagSet } from '@/types';

/**
 * Converts a kebab-case string to camelCase.
 * LaunchDarkly's useFlags() hook automatically converts flag keys to camelCase.
 * @see https://launchdarkly.com/docs/sdk/client-side/react/react-web
 */
const toCamelCase = (str: string): string =>
  str.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());

// Pre-compute key mapping at module level
const FLAG_KEYS = Object.keys(FEATURE_FLAG_DEFAULTS) as (keyof FeatureFlagSet)[];
const CAMEL_KEYS = FLAG_KEYS.map(toCamelCase);

/**
 * Returns the current feature flag values.
 *
 * Uses LaunchDarkly's useFlags() hook which handles reactivity internally.
 * Stabilizes output reference to prevent unnecessary re-renders.
 */
export const useFeatureFlags = (): FeatureFlagSet => {
  const ldFlags = useFlags();
  const prevRef = useRef<FeatureFlagSet>(FEATURE_FLAG_DEFAULTS);

  // Build current flags
  const current = { ...FEATURE_FLAG_DEFAULTS } as FeatureFlagSet;
  for (let i = 0; i < FLAG_KEYS.length; i++) {
    const ldValue = ldFlags[CAMEL_KEYS[i]];
    if (ldValue !== undefined) {
      current[FLAG_KEYS[i]] = ldValue as boolean;
    }
  }

  // Only update ref if values actually changed
  const hasChanged = FLAG_KEYS.some((key) => current[key] !== prevRef.current[key]);
  if (hasChanged) {
    prevRef.current = current;
  }

  return prevRef.current;
};
